/**
 * Portfolio Composer Component
 * Full-screen modal for interactive portfolio composition and scenario analysis
 */

import React, { useState, useEffect } from 'react';
import { VerifiedConstraints, type RiskDelta, type VerifiedConstraint } from './VerifiedConstraints';
import { X, Plus, RefreshCw, Save, FileText } from 'lucide-react';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import TransactionBuilder from './TransactionBuilder';
import PortfolioSnapshot from './PortfolioSnapshot';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'sonner';

interface Position {
  symbol?: string;
  isin?: string;
  name?: string;
  security_name?: string;
  quantity: number;
  market_value: number;
  market_price: number;
  currency: string;
  asset_class?: string;
  unrealized_pl?: number;
}

interface Transaction {
  type: 'buy' | 'sell' | 'add_cash';
  symbol?: string;
  name?: string;
  quantity?: number;
  price?: number;
  currency?: string;
  amount?: number;
}

interface CashPosition {
  currency: string;
  amount: number;
  base_amount?: number;
}

interface PortfolioData {
  positions: Position[];
  cash: CashPosition[];
  total_value: number;
  health_score: number;
  allocations: {
    by_asset_class: Record<string, number>;
    by_currency: Record<string, number>;
    by_sector: Record<string, number>;
    total_value: number;
  };
  risk_metrics: {
    volatility: number;
    var_1day: number;
    concentration_risk: number;
  };
}

interface PortfolioComposerProps {
  clientId: string;
  portfolioId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void; // Callback when proposal is saved successfully
  // TASK-396: pre-fill from an AI-suggested proposal — seeds transactions/name/notes
  // and auto-runs the simulation on open so the advisor sees the impact immediately.
  initialTransactions?: Transaction[];
  initialProposalName?: string;
  initialProposalNotes?: string;
  // TASK-686: the house rails the backend verified on these trades, and the risk change per
  // substitution. The advisor reviews the FIGURES here, not the model's prose rationale.
  verifiedConstraints?: VerifiedConstraint[];
  riskDeltas?: RiskDelta[];
}

const PortfolioComposer: React.FC<PortfolioComposerProps> = ({ clientId, portfolioId, isOpen, onClose, onSaveSuccess, initialTransactions, initialProposalName, initialProposalNotes, verifiedConstraints, riskDeltas }) => {
  const [currentPortfolio, setCurrentPortfolio] = useState<PortfolioData | null>(null);
  const [simulatedPortfolio, setSimulatedPortfolio] = useState<PortfolioData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTransactionBuilder, setShowTransactionBuilder] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [proposalName, setProposalName] = useState('');
  const [proposalNotes, setProposalNotes] = useState('');
  const [hasRunSimulation, setHasRunSimulation] = useState(false);
  const [simulationFees, setSimulationFees] = useState<{
    total_commission: number;
    total_stamp_duty: number;
    total_fees: number;
  } | null>(null);

  // Load initial portfolio. TASK-396: when opened with AI-suggested trades, seed the
  // form and auto-run the simulation so the advisor sees the impact right away.
  useEffect(() => {
    if (!isOpen || !clientId) return;
    if (initialTransactions?.length) setTransactions(initialTransactions);
    if (initialProposalName) setProposalName(initialProposalName);
    if (initialProposalNotes) setProposalNotes(initialProposalNotes);
    loadPortfolio(initialTransactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clientId]);

  // Handle Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !showSaveDialog) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, showSaveDialog, onClose]);

  const loadPortfolio = async (autoSimulateTxns?: Transaction[]) => {
    setLoading(true);
    setError(null);
    try {
      // Simulate with empty transactions to get current state
      const response = await portfolioApiService.simulatePortfolio(clientId, [], portfolioId);
      // Both start with the same current portfolio data - use deep copy to avoid reference issues
      setCurrentPortfolio(response.current);
      setSimulatedPortfolio(JSON.parse(JSON.stringify(response.current)));
      // TASK-396: auto-run the AI-seeded simulation (state may not be set yet, so pass explicitly)
      if (autoSimulateTxns?.length) {
        await runSimulation(autoSimulateTxns);
      }
    } catch (err) {
      setError('Failed to load portfolio');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async (txns: Transaction[] = transactions) => {
    if (txns.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await portfolioApiService.simulatePortfolio(clientId, txns, portfolioId);
      // Only update simulated portfolio, keep current unchanged
      setSimulatedPortfolio(response.simulated);
      setSimulationFees(response.fees || null);
      setHasRunSimulation(true);
    } catch (err) {
      setError('Simulation failed');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = (transaction: Transaction) => {
    setTransactions([...transactions, transaction]);
    setHasRunSimulation(false); // Reset when new transaction added
  };

  const removeTransaction = (index: number) => {
    setTransactions(transactions.filter((_, i) => i !== index));
    setHasRunSimulation(false); // Reset when transaction removed
  };

  const clearTransactions = () => {
    setTransactions([]);
    // Reset simulated portfolio to match current portfolio
    setSimulatedPortfolio(currentPortfolio ? {...currentPortfolio} : null);
    setHasRunSimulation(false);
    setSimulationFees(null);
  };

  const saveProposal = async () => {
    if (!proposalName.trim()) {
      alert('Please enter a proposal name');
      return;
    }

    setLoading(true);
    try {
      const proposalData = {
        name: proposalName,
        type: 'rebalancing',
        current_portfolio: currentPortfolio as unknown as Record<string, unknown>,
        proposed_portfolio: simulatedPortfolio as unknown as Record<string, unknown>,
        transactions: transactions,
        current_metrics: {
          total_value: currentPortfolio?.total_value,
          health_score: currentPortfolio?.health_score,
          allocations: currentPortfolio?.allocations
        },
        proposed_metrics: {
          total_value: simulatedPortfolio?.total_value,
          health_score: simulatedPortfolio?.health_score,
          allocations: simulatedPortfolio?.allocations
        },
        rationale: proposalNotes,
        advisor_notes: proposalNotes,
        total_commission: simulationFees?.total_commission || 0,
        total_stamp_duty: simulationFees?.total_stamp_duty || 0,
        total_fees: simulationFees?.total_fees || 0
      };

      await portfolioApiService.saveProposal(clientId, proposalData);

      toast.success('Proposal saved successfully!');
      setShowSaveDialog(false);
      setProposalName('');
      setProposalNotes('');
      
      // Call the success callback if provided
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err) {
      console.error('Failed to save proposal:', err);
      alert('Failed to save proposal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full h-full bg-white flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Portfolio Composer</h2>
            {/* TASK-686 review: this printed the raw client UUID at the RM's decision point.
                Every surface that opens the composer already names the client in the frame
                above it, so the id was noise the advisor had to read past. */}
            <p className="text-gray-600">Interactive scenario analysis</p>
          </div>
          <div className="flex items-center gap-4">
            {hasRunSimulation && transactions.length > 0 && simulatedPortfolio && (
              <button
                onClick={() => setShowSaveDialog(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Proposal
              </button>
            )}
            <button
              onClick={clearTransactions}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Current Portfolio */}
          <div className="w-1/2 border-r flex flex-col">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="font-semibold text-lg">Current Portfolio</h3>
            </div>
            {currentPortfolio && (
              <PortfolioSnapshot
                portfolio={currentPortfolio}
              />
            )}
          </div>

          {/* Right Panel - Simulated Portfolio */}
          <div className="w-1/2 flex flex-col">
            <div className="px-6 py-4 border-b bg-blue-50">
              <h3 className="font-semibold text-lg">Simulated Portfolio</h3>
            </div>
            {simulatedPortfolio && (
              <PortfolioSnapshot
                portfolio={simulatedPortfolio}
                isSimulated={true}
                currentValue={currentPortfolio?.total_value}
              />
            )}
          </div>
        </div>

        {/* Transaction Bar */}
        <div className="border-t px-6 py-4">
          {/* TASK-686 deep review: the RM must see the verification before deciding whether
              to run, edit, or save the proposal. Keeping it only inside the Save dialog hid
              the central trust artefact until after the decision point. */}
          <VerifiedConstraints
            constraints={verifiedConstraints}
            riskDeltas={riskDeltas}
            className="mb-4"
          />

          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Transactions</h3>
            <button
              onClick={() => setShowTransactionBuilder(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Transaction
            </button>
          </div>
          
          {transactions.length > 0 ? (
            <div className="flex gap-2 flex-wrap">
              {transactions.map((tx, index) => (
                <TransactionChip 
                  key={index}
                  transaction={tx}
                  onRemove={() => removeTransaction(index)}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No transactions added. Click "Add Transaction" to start.</p>
          )}

          {transactions.length > 0 && (
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={() => runSimulation()}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                disabled={loading}
              >
                {loading ? 'Running...' : 'Run Simulation'}
              </button>
              
              {simulationFees && (
                <div className="flex gap-4 text-sm">
                  <span className="text-gray-600">
                    Commission: <span className="font-semibold">{formatCurrency(simulationFees.total_commission)}</span>
                  </span>
                  <span className="text-gray-600">
                    Stamp Duty: <span className="font-semibold">{formatCurrency(simulationFees.total_stamp_duty)}</span>
                  </span>
                  <span className="text-gray-700 font-medium">
                    Total Fees: <span className="font-bold text-red-600">{formatCurrency(simulationFees.total_fees)}</span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Transaction Builder Modal */}
        {showTransactionBuilder && (
          <TransactionBuilder
            onAdd={(transaction: any) => {
              addTransaction(transaction);
              setShowTransactionBuilder(false);
            }}
            onClose={() => setShowTransactionBuilder(false)}
            currentPositions={currentPortfolio?.positions.map(p => ({
              symbol: p.symbol || p.isin || '',
              quantity: p.quantity,
              name: p.security_name || p.name || '',
              price: p.market_price || 0
            })) || []}
          />
        )}

        {/* Error Display */}
        {error && (
          <div className="absolute top-20 right-6 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
            {error}
          </div>
        )}

        {/* Save Proposal Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 z-70 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-[500px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Save Portfolio Proposal
                </h3>
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Proposal Name</label>
                  <input
                    type="text"
                    value={proposalName}
                    onChange={(e) => setProposalName(e.target.value)}
                    placeholder="e.g., Q1 2024 Rebalancing"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Notes / Rationale</label>
                  <textarea
                    value={proposalNotes}
                    onChange={(e) => setProposalNotes(e.target.value)}
                    placeholder="Explain the reasoning behind these changes..."
                    rows={4}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* TASK-686: what was actually checked, above the prose. A breach here means
                    the trades never reached this screen from the AI path — but a manually
                    edited proposal can still show one. */}
                <VerifiedConstraints
                  constraints={verifiedConstraints}
                  riskDeltas={riskDeltas}
                />

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Summary:</p>
                  <ul className="text-sm space-y-1">
                    <li>• {transactions.length} transaction(s) planned</li>
                    <li>• Current value: {formatCurrency(currentPortfolio?.total_value || 0)}</li>
                    <li>• Projected value: {formatCurrency(simulatedPortfolio?.total_value || 0)}
                      {simulatedPortfolio && currentPortfolio && (
                        <span className={simulatedPortfolio.total_value > currentPortfolio.total_value ? 'text-green-600' : 
                                       simulatedPortfolio.total_value < currentPortfolio.total_value ? 'text-red-600' : 'text-gray-600'}>
                          {' '}({simulatedPortfolio.total_value > currentPortfolio.total_value ? '+' : ''}
                          {formatCurrency(simulatedPortfolio.total_value - currentPortfolio.total_value)})
                        </span>
                      )}
                    </li>
                    <li>• Health score: {currentPortfolio?.health_score.toFixed(0)}% → {simulatedPortfolio?.health_score.toFixed(0)}%</li>
                    {simulationFees && (
                      <>
                        <li>• Commission: {formatCurrency(simulationFees.total_commission)}</li>
                        <li>• Stamp duty: {formatCurrency(simulationFees.total_stamp_duty)}</li>
                        <li className="font-semibold">• Total fees: {formatCurrency(simulationFees.total_fees)}</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={saveProposal}
                  disabled={loading || !proposalName.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Proposal'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Transaction Chip Component
const TransactionChip: React.FC<{
  transaction: Transaction;
  onRemove: () => void;
}> = ({ transaction, onRemove }) => {
  const getChipColor = () => {
    switch (transaction.type) {
      case 'buy': return 'bg-green-100 text-green-800';
      case 'sell': return 'bg-red-100 text-red-800';
      case 'add_cash': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLabel = () => {
    if (transaction.type === 'add_cash') {
      return `Add ${transaction.amount} ${transaction.currency}`;
    }
    return `${transaction.type.toUpperCase()} ${transaction.quantity} ${transaction.symbol} @ ${transaction.price}`;
  };

  return (
    <div className={`px-3 py-1 rounded-full flex items-center gap-2 ${getChipColor()}`}>
      <span className="text-sm">{getLabel()}</span>
      <button
        onClick={onRemove}
        className="hover:opacity-70"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};


export default PortfolioComposer;
